// Orders (sales history): opens on every till in the store for today. Till, date
// range and status are filters; search goes to the server (bill no, customer,
// mobile, all dates). Picking a bill loads its lines through Core's
// getSalesHistoryData and offers Reprint, Send e-receipt, Gift receipt and
// Return or exchange — the same handlers as before. Bills this till saved but
// hasn't synced yet show on top as "Pending sync", read-only.
import React, { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { formatMoney } from "../../../../utils/fashion/money";
import { orderReturnable } from "../../../../utils/fashion/returnLines";
import { buildGiftReceiptHtml, giftReceiptLines, printGiftReceipt } from "../../../../utils/fashion/giftReceipt";
import { buildSendReceiptMutation, canSendReceipt, readSendReceipt, RECEIPT_CHANNELS } from "../../../../utils/fashion/digitalReceipt";
import { billLineViews, idsNeedingVariant, loadVariantLabels } from "../../../../utils/fashion/billLines";
import { reprintOrder } from "../../../../utils/fashion/orderReprint";
import {
  ALL_TILLS,
  THIS_TILL,
  ORDERS_PAGE,
  ORDER_RANGES,
  ORDER_STATUSES,
  PENDING_SYNC,
  buildSalesHistoryQuery,
  dateRangeFor,
  filterOrders,
  isBeingShipped,
  mapPendingOrder,
  mergeOrderPages,
  readSalesHistoryRows,
  summarizeOrders,
  tillNames,
  withPendingOrders,
} from "../../../../utils/fashion/ordersQuery";
import { readPendingSyncOrders } from "../../../../utils/dataIntegrity";
import db from "../../../../database";
import { fashionPosService, orderHistoryService } from "../../../../services";
import { getCurrentTillId } from "../../../../utils/tillIdentity";
import ShipToHomeForm from "../shipToHome/ShipToHomeForm";
import EmptyState from "../EmptyState";

const STATUS_TONE = { Completed: "ok", Returned: "info", Cancelled: "neu", [PENDING_SYNC]: "warn" };
const isReturnRow = (o) => o?.isReturn === "Y" || o?.isReturn === true;

function orderWhen(o) {
  const date = o.orderDate ? dayjs(o.orderDate) : null;
  const t = o.orderTime ? dayjs(o.orderTime) : null;
  const time = t?.isValid() ? t.format("HH:mm") : String(o.orderTime || "").slice(0, 5);
  const d = date?.isValid() ? date : t?.isValid() ? t : null;
  if (!d) return time;
  const day = d.isSame(dayjs(), "day") ? "Today" : d.isSame(dayjs().subtract(1, "day"), "day") ? "Yesterday" : d.format("DD MMM");
  return time ? `${day} · ${time}` : day;
}

const custOf = (o) => (o?.customer && typeof o.customer === "object" ? o.customer : null);
const initials = (name) =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

export default function OrdersView({ online, tillData, posConfig, getSalesHistoryData, dispatchEvent, upsertPOSLog, startReturn, customerLabel, precision, ...rest }) {
  const [range, setRange] = useState("today");
  const [till, setTill] = useState(ALL_TILLS);
  const [status, setStatus] = useState("All");
  const [term, setTerm] = useState("");
  const [searched, setSearched] = useState("");
  const [rows, setRows] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nextRow, setNextRow] = useState(null);
  const [selId, setSelId] = useState(null);
  const [details, setDetails] = useState({});
  const [variants, setVariants] = useState({});
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [shipOpen, setShipOpen] = useState(false);
  const [shipped, setShipped] = useState({});
  const [toast, setToast] = useState(null);
  const request = useRef(0);
  const detailReq = useRef(0);
  const toastTimer = useRef(null);
  const thisTillName = tillData?.tillAccess?.cwrTill?.till || "";
  const prec = precision ?? tillData?.tillAccess?.csBunit?.currencies?.[0]?.prcPrecision ?? 2;

  // One message at a time; a new one replaces the last.
  const say = (text, tone = "ok") => {
    clearTimeout(toastTimer.current);
    setToast({ text, tone });
    toastTimer.current = setTimeout(() => setToast(null), 3600);
  };
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const load = (startRow = 0) => {
    const id = ++request.current;
    const { from, to } = dateRangeFor(range);
    const tillId = till === THIS_TILL ? getCurrentTillId() : null;
    setLoading(true);
    if (startRow === 0) {
      setNextRow(null);
      // Local read only (indexed anyOf, works on Dexie and SQLite); a failure just shows no pending rows.
      readPendingSyncOrders(db)
        .then((list) => id === request.current && setPending((list || []).map((o) => mapPendingOrder(o, { precision: prec, tillName: thisTillName }))))
        .catch(() => id === request.current && setPending([]));
    }
    Promise.resolve(orderHistoryService(buildSalesHistoryQuery({ from, to, tillId, storeId: tillData?.tillAccess?.csBunit?.csBunitId, q: searched, startRow })))
      .then((res) => {
        if (id !== request.current) return;
        const page = readSalesHistoryRows(res, { precision: prec, posConfig });
        if (!page) {
          if (startRow === 0) setRows([]);
          say("Couldn't load orders. Check the connection and try again.", "bad");
          return;
        }
        setRows((prev) => (startRow === 0 ? page : mergeOrderPages(prev, page)));
        setNextRow(page.length >= ORDERS_PAGE ? startRow + ORDERS_PAGE : null);
      })
      .catch(() => id === request.current && say("Couldn't load orders. Check the connection and try again.", "bad"))
      .finally(() => id === request.current && setLoading(false));
  };

  // A named till is filtered on the loaded rows; "all" and "this till" ask the server.
  const serverTill = till === THIS_TILL ? THIS_TILL : ALL_TILLS;
  useEffect(() => {
    if (online) load(0);
  }, [online, range, serverTill, searched]); // eslint-disable-line react-hooks/exhaustive-deps

  const allRows = useMemo(() => withPendingOrders(rows, searched ? filterOrders(pending, { q: searched }) : pending, dateRangeFor(range)), [rows, pending, searched, range]);
  const visible = useMemo(() => filterOrders(allRows, { till, status, q: term.trim() === searched ? "" : term }), [allRows, till, status, term, searched]);
  const showPaidBy = visible.some((o) => o.paidBy);
  const stats = useMemo(() => summarizeOrders(visible), [visible]);
  const tillOptions = useMemo(() => tillNames(rows).filter((n) => n !== thisTillName), [rows, thisTillName]);
  const sel = visible.find((o) => o.key === selId) || null;
  const detail = sel ? details[sel.sOrderID] : null;
  const order = sel && detail ? { ...sel, ...detail } : sel;

  const pick = async (o) => {
    setSelId(o.key);
    setReceiptOpen(false);
    setShipOpen(false);
    if (o.pending) {
      if (!details[o.sOrderID]) setDetails((prev) => ({ ...prev, [o.sOrderID]: { items: o.items, paymentData: o.paymentData } }));
      return;
    }
    if (details[o.sOrderID] || typeof getSalesHistoryData !== "function") return;
    const id = ++detailReq.current;
    try {
      const data = await getSalesHistoryData(o.sOrderID);
      const full = Array.isArray(data) ? data[0] : null;
      if (!full) {
        if (id === detailReq.current) say("Couldn't load this bill's items. Try again.", "bad");
        return;
      }
      setDetails((prev) => ({ ...prev, [o.sOrderID]: full }));
      const ids = idsNeedingVariant(full.items).filter((pid) => !(pid in variants));
      if (ids.length) loadVariantLabels(ids).then((found) => setVariants((prev) => ({ ...prev, ...found })));
    } catch {
      if (id === detailReq.current) say("Couldn't load this bill's items. Try again.", "bad");
    }
  };

  const submitSearch = (e) => {
    e.preventDefault();
    setSearched(term.trim());
  };
  const reset = () => {
    setTerm("");
    setSearched("");
    setTill(ALL_TILLS);
    setRange("today");
    setStatus("All");
  };

  // Already on all tills, today, no search: "reset" would change nothing, so widen the dates instead.
  const atDefaults = !searched && till === ALL_TILLS && range === "today" && status === "All";

  if (!online) {
    return <EmptyState icon="offline" title="You're offline" hint="Orders need a connection. Reconnect the till, then open Orders again." />;
  }

  const run = async (key, fn) => {
    if (busy) return;
    setBusy(key);
    try {
      await fn();
    } finally {
      setBusy("");
    }
  };

  const reprint = () =>
    run("reprint", async () => {
      if (!detail) return say("This bill's details are still loading. Try again in a moment.", "warn");
      const res = await reprintOrder(order, { tillData, posConfig, dispatchEvent, upsertPOSLog });
      say(res.message, res.tone);
    });

  const sendReceipt = (via) =>
    run("send", async () => {
      let res;
      try {
        res = readSendReceipt(await fashionPosService(buildSendReceiptMutation(order.sOrderID, via)));
      } catch {
        res = { ok: false, message: "Couldn't send the receipt. Check the connection and try again." };
      }
      if (res.ok) {
        setReceiptOpen(false);
        say(`${res.message || "Receipt sent"} for ${order.documentno}.`, "ok");
      } else say(res.message, "bad");
    });

  const giftReceipt = () =>
    run("gift", async () => {
      const res = await printGiftReceipt(buildGiftReceiptHtml(order, tillData));
      if (res.ok) say(`Gift receipt for ${order.documentno} sent to the printer.`, "ok");
      else say(res.message, "bad");
    });

  const beginReturn = () =>
    run("return", async () => {
      try {
        const res = await startReturn(order, { ...rest, posConfig, dispatchEvent });
        if (res?.error) say(res.error, "warn");
      } catch {
        say("Couldn't open this bill for return. Check the connection and try again.", "bad");
      }
    });

  const lines = detail ? billLineViews(detail.items, variants) : [];
  const c = custOf(order);
  const paidBy = order?.paidBy || (detail?.paymentData || []).map((p) => p?.name).filter(Boolean).join(" + ");
  // A bill marked to ship can't be returned here: the goods haven't reached the customer.
  const shipping = Boolean(order) && !order.pending && (isBeingShipped(order) || Boolean(shipped[order.sOrderID]));
  const canReturn = Boolean(order) && !order.pending && !isReturnRow(order) && Boolean(detail) && !shipping && orderReturnable(order, posConfig) && typeof rest.searchReturnHistory === "function";
  // Ship to home only for a completed sale not already being delivered (the server also refuses bills with returns).
  const canShip = Boolean(order) && !order.pending && Boolean(detail) && order.status === "Completed" && !isReturnRow(order) && !shipping;
  const returnText = posConfig?.allowReturnExchange === "Y" ? "Return or exchange" : "Return";

  return (
    <div className="fp-bills-split">
      <div className="fp-bills-main">
        {toast && (
          <p className={`fp-bills-toast is-${toast.tone}`} role={toast.tone === "bad" ? "alert" : "status"}>
            {toast.text}
          </p>
        )}
        <form className="fp-bills-bar" onSubmit={submitSearch}>
          <input className="fp-input fp-bills-search" value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Bill no., customer or mobile" aria-label="Search orders" />
          <label className="fp-bills-field">
            <span className="fp-bills-lbl">Till</span>
            <select className="fp-input fp-bills-select" value={till} onChange={(e) => setTill(e.target.value)} aria-label="Till">
              <option value={ALL_TILLS}>All tills in store</option>
              <option value={THIS_TILL}>{thisTillName ? `${thisTillName} (this till)` : "This till"}</option>
              {tillOptions.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        </form>
        <div className="fp-bills-bar">
          <div className="fp-bills-field">
            <span className="fp-bills-lbl">Date</span>
            <div className="fp-bills-seg" role="group" aria-label="Date">
              {ORDER_RANGES.map((r) => (
                <button key={r.key} type="button" className={range === r.key ? "is-on" : ""} aria-pressed={range === r.key} disabled={Boolean(searched)} onClick={() => setRange(r.key)}>
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          <div className="fp-bills-field">
            <span className="fp-bills-lbl">Status</span>
            <div className="fp-bills-seg" role="group" aria-label="Status">
              {ORDER_STATUSES.map((s) => (
                <button key={s} type="button" className={status === s ? "is-on" : ""} aria-pressed={status === s} onClick={() => setStatus(s)}>
                  {s}
                </button>
              ))}
            </div>
          </div>
          <button type="button" className="fp-btn-ghost fp-btn-sm fp-bills-reset" onClick={reset}>
            Reset filters
          </button>
        </div>
        {searched && <p className="fp-bills-meta" role="status">{`Results for “${searched}”, all dates`}</p>}
        <section className="fp-bills-stats fp-num" aria-label="Summary">
          <div>
            <span>Bills</span>
            <b>{stats.bills}</b>
          </div>
          <div>
            <span>Net sales</span>
            <b>{formatMoney(stats.net, tillData)}</b>
          </div>
          <div>
            <span>Returns</span>
            <b>{formatMoney(stats.returns, tillData)}</b>
          </div>
          <div>
            <span>Average bill</span>
            <b>{formatMoney(stats.average, tillData)}</b>
          </div>
        </section>
        <div className="fp-bills-scroll fp-bills-tbox" aria-busy={loading ? "true" : "false"}>
          {visible.length === 0 ? (
            loading ? (
              <div className="fp-empty fp-bills-none">
                <b>Loading orders…</b>
              </div>
            ) : (
              <EmptyState
                icon="search"
                title="No bills for these filters"
                hint={till !== ALL_TILLS ? "You are looking at one till only. Other tills may have bills." : "Try a wider date range or clear the search."}
                action={atDefaults ? { label: "Show last 7 days", onClick: () => setRange("last7") } : { label: "Show all tills, today", onClick: reset }}
              />
            )
          ) : (
            // The list resolver does not always return the receipt plan; a
            // column of dashes says nothing, so Paid by shows only when known.
            <table className="fp-bills-tbl fp-num">
              <thead>
                <tr>
                  <th>Bill no</th>
                  <th>Date · time</th>
                  <th>Till</th>
                  <th>Customer</th>
                  <th className="is-r">Items</th>
                  <th className="is-r">Amount</th>
                  {showPaidBy && <th>Paid by</th>}
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((o) => (
                  <tr key={o.key} className={o.key === selId ? "is-on" : ""} onClick={() => pick(o)} tabIndex={0} onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), pick(o))} aria-selected={o.key === selId}>
                    <td>
                      <b>{o.documentno}</b>
                    </td>
                    <td>{orderWhen(o)}</td>
                    <td>{o.tillName || "—"}</td>
                    <td>
                      {customerLabel(o.customer)}
                      {custOf(o)?.mobileNo && <div className="fp-bills-meta">{custOf(o).mobileNo}</div>}
                    </td>
                    <td className="is-r">{o.totalQty}</td>
                    <td className="is-r">{formatMoney(o.total, tillData)}</td>
                    {showPaidBy && <td>{o.paidBy || "—"}</td>}
                    <td>
                      <span className={`fp-bills-pill is-${STATUS_TONE[o.status] || "neu"}`}>{o.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {nextRow !== null && visible.length > 0 && (
            <div className="fp-grid-foot">
              <span className="fp-muted fp-num">Showing {rows.length} orders</span>
              <button type="button" className="fp-btn-ghost fp-btn-sm" disabled={loading} onClick={() => load(nextRow)}>
                Show more
              </button>
            </div>
          )}
        </div>
      </div>
      <aside className="fp-bills-side" aria-label="Bill detail">
        {!order ? (
          <EmptyState icon="pick" title="Pick a bill" hint="Its items and actions show here." />
        ) : (
          <>
            <div className="fp-bills-side-head">
              <h3 className="fp-num">{order.documentno}</h3>
              {/* The list row's status and time: the detail fetch returns its own (e.g. "Success", a date-only time). */}
              <span className={`fp-bills-pill is-${STATUS_TONE[sel.status] || "neu"}`}>{sel.status}</span>
            </div>
            <span className="fp-bills-meta fp-num">
              {orderWhen(sel)}
              {order.tillName ? ` · ${order.tillName}` : ""}
            </span>
            <div className="fp-bills-cust">
              <span className="fp-bills-av" aria-hidden="true">
                {initials(customerLabel(order.customer)) || "W"}
              </span>
              <span>
                <b>{customerLabel(order.customer)}</b>
                <span className="fp-bills-meta fp-num">{c?.mobileNo || "No mobile on this bill"}</span>
              </span>
            </div>
            {!detail ? (
              <p className="fp-bills-meta">Loading items…</p>
            ) : (
              <table className="fp-bills-tbl fp-num" aria-label="Items">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th className="is-r">Qty</th>
                    <th className="is-r">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l) => (
                    <tr key={l.key}>
                      <td>
                        <b>{l.name}</b>
                        {l.variant && <div className="fp-bills-meta">{l.variant}</div>}
                      </td>
                      <td className="is-r">{l.qty}</td>
                      <td className="is-r">{formatMoney(l.amount, tillData)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="fp-bills-side-total">
              <span className="fp-bills-meta">{paidBy ? `Paid by ${paidBy}` : ""}</span>
              <b className="fp-num">{formatMoney(order.total, tillData)}</b>
            </div>
            {order.pending && <p className="fp-bills-note">Saved on this till, not yet uploaded. Reprint, return and receipts open once it syncs.</p>}
            <div className="fp-bills-acts is-wrap" hidden={order.pending}>
              <button type="button" className="fp-btn-ghost fp-btn-sm" disabled={!detail || Boolean(busy)} onClick={reprint}>
                {busy === "reprint" ? "Printing…" : "Reprint"}
              </button>
              {canSendReceipt(order) &&
                (receiptOpen ? (
                  RECEIPT_CHANNELS.map((ch) => (
                    <button key={ch.value} type="button" className="fp-btn-ghost fp-btn-sm" disabled={Boolean(busy)} aria-label={`Send ${order.documentno} on ${ch.label}`} onClick={() => sendReceipt(ch.value)}>
                      {busy === "send" ? "Sending…" : ch.label}
                    </button>
                  ))
                ) : (
                  <button type="button" className="fp-btn-ghost fp-btn-sm" disabled={Boolean(busy)} onClick={() => setReceiptOpen(true)}>
                    Send e-receipt
                  </button>
                ))}
              {detail && !isReturnRow(order) && giftReceiptLines(order).length > 0 && (
                <button type="button" className="fp-btn-ghost fp-btn-sm" disabled={Boolean(busy)} onClick={giftReceipt}>
                  Gift receipt
                </button>
              )}
              {canShip && !shipOpen && (
                <button type="button" className="fp-btn-ghost fp-btn-sm" disabled={Boolean(busy) || !online} title={online ? undefined : "Shipping needs a connection"} onClick={() => setShipOpen(true)}>
                  Ship to home
                </button>
              )}
              {detail && shipping && !isReturnRow(order) && <span className="fp-bills-note">Being shipped to the customer. Return it once it's delivered.</span>}
              {canReturn && (
                <button type="button" className="fp-bills-pri" disabled={Boolean(busy)} onClick={beginReturn}>
                  {busy === "return" ? "Opening…" : returnText}
                </button>
              )}
            </div>
            {canShip && shipOpen && (
              <ShipToHomeForm
                orderId={order.sOrderID}
                documentNo={order.documentno}
                bunitId={tillData?.tillAccess?.csBunit?.csBunitId}
                customer={c}
                isOnline={online}
                onDone={(r) => {
                  say(`${order.documentno}: ${r.message}`, "ok");
                  setShipped((m) => ({ ...m, [order.sOrderID]: true }));
                  setShipOpen(false);
                }}
                onError={(r) => say(r.message, "bad")}
                onCancel={() => setShipOpen(false)}
              />
            )}
          </>
        )}
      </aside>
    </div>
  );
}
